import { useState, type ReactNode } from 'react'
import { Link } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { Tags } from 'lucide-react'
import { fetchBudget, type BudgetPeriodType } from '@/api/budget'
import { BudgetPeriodControls } from '@/components/BudgetPeriodControls'
import { EmptyState } from '@/components/ui/empty-state'
import { useFormatCurrency } from '@/hooks/useFormatCurrency'
import { useHousehold } from '@/hooks/useHousehold'
import {
  convertPeriod,
  currentPeriodValue,
  formatPeriodLabel,
  periodBounds,
  periodTransactionFilter,
  shiftPeriod,
  type FyStartMonth,
} from '@/lib/budgetPeriod'
import { compareWithPrevious, type CategoryChangeRow } from '@/lib/categoryChange'
import { CategoryIcon } from '@/lib/categoryIcon'
import { fromPaise, toPaise } from '@/lib/splitMath'
import { transactionsLink } from '@/lib/transactionsLink'
import { cn } from '@/lib/utils'
import { ROUTES } from '@/routes/routes'

type Kind = 'EXPENSE' | 'INCOME'

const KINDS: { value: Kind; label: string }[] = [
  { value: 'EXPENSE', label: 'Spending' },
  { value: 'INCOME', label: 'Income' },
]

const DAY_MS = 24 * 60 * 60 * 1000

/** Days so far in the current period; the whole period otherwise. */
function daysCounted(type: BudgetPeriodType, period: string, fy: FyStartMonth): number {
  const { start, end } = periodBounds(type, period, fy)
  const today = new Date()
  const last = today >= start && today <= new Date(end.getTime() + DAY_MS - 1) ? today : end
  return Math.floor((last.getTime() - start.getTime()) / DAY_MS) + 1
}

export function CategoryBreakdownTab() {
  const { formatWhole } = useFormatCurrency()
  const { data: household } = useHousehold()
  const fy = household?.financialYearStartMonth ?? 4
  const [periodType, setPeriodType] = useState<BudgetPeriodType>('MONTHLY')
  const [period, setPeriod] = useState(() => currentPeriodValue('MONTHLY', fy))
  const [kind, setKind] = useState<Kind>('EXPENSE')

  function handlePeriodTypeChange(nextType: BudgetPeriodType) {
    setPeriod((current) => convertPeriod(periodType, current, nextType, fy))
    setPeriodType(nextType)
  }

  // Same key as the Budget page, so moving between the two reuses the fetch.
  const { data, isLoading, isError } = useQuery({
    queryKey: ['budget', periodType, period, fy],
    queryFn: () => fetchBudget({ periodType, period }),
  })

  const isSpending = kind === 'EXPENSE'
  const breakdown = isSpending ? data?.spendingByCategory : data?.incomeByCategory
  const previous = isSpending ? data?.previousSpendingByCategory : data?.previousIncomeByCategory
  const { rows, total, previousTotal, grewMost } = compareWithPrevious(breakdown ?? [], previous)
  const largest = rows[0]?.total ?? 0
  const periodLabel = formatPeriodLabel(periodType, period, fy)
  const fullPreviousLabel = formatPeriodLabel(
    periodType,
    shiftPeriod(periodType, period, -1, fy),
    fy
  )
  // "less than September" reads better than "less than September 2026".
  const previousLabel =
    periodType === 'MONTHLY' ? fullPreviousLabel.replace(/ \d{4}$/, '') : fullPreviousLabel
  const totalChange =
    previousTotal != null ? fromPaise(toPaise(total) - toPaise(previousTotal)) : null
  const perDay = fromPaise(Math.round(toPaise(total) / daysCounted(periodType, period, fy)))
  const pct = (amount: number) => `${Math.round((amount / total) * 100)}%`

  // Spending going up is bad news, income going up is good news.
  const changeTone = (change: number) =>
    change === 0
      ? 'text-[var(--fl-text-2)]'
      : change > 0 === isSpending
        ? 'text-[var(--fl-expense)]'
        : 'text-[var(--fl-income)]'

  function changeText(r: CategoryChangeRow): string | null {
    if (r.change == null) return null
    if (r.isNew) return 'New'
    if (r.change === 0) return 'Same'
    return `${r.change > 0 ? '↑' : '↓'} ${formatWhole(Math.abs(r.change))}`
  }

  return (
    <div className="flex flex-col gap-5">
      <div className="flex flex-wrap items-center gap-3">
        <BudgetPeriodControls
          fyStartMonth={fy}
          disabled={!household}
          periodType={periodType}
          period={period}
          onPeriodTypeChange={handlePeriodTypeChange}
          onPeriodChange={setPeriod}
        />
        <div
          role="radiogroup"
          aria-label="Spending or income"
          className="inline-flex items-center gap-1 rounded-full border border-[var(--fl-border)] bg-[var(--fl-surface-subtle)] p-1"
        >
          {KINDS.map((k) => {
            const selected = k.value === kind
            return (
              <button
                key={k.value}
                type="button"
                role="radio"
                aria-checked={selected}
                onClick={() => setKind(k.value)}
                className={cn(
                  'rounded-full px-3 py-1.5 text-xs font-semibold transition-colors',
                  'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
                  selected
                    ? 'bg-[var(--fl-surface)] text-[var(--fl-primary-text)] shadow-sm'
                    : 'text-[var(--fl-text-2)] hover:text-[var(--fl-text-1)]'
                )}
              >
                {k.label}
              </button>
            )
          })}
        </div>
      </div>

      {isLoading && (
        <div className="h-40 animate-pulse rounded-[20px] bg-[var(--fl-surface-subtle)]" />
      )}

      {(isError || (data && !breakdown)) && (
        <div className="rounded-xl border border-[var(--fl-border)] bg-[var(--fl-surface)] p-6 text-sm text-[var(--fl-error)]">
          Failed to load the category breakdown.
        </div>
      )}

      {breakdown && rows.length === 0 && (
        <EmptyState
          icon={Tags}
          title={isSpending ? `Nothing spent in ${periodLabel}` : `No income in ${periodLabel}`}
          description="Pick another period, or add transactions with a category to see the split here."
        />
      )}

      {rows.length > 0 && (
        <>
          <section
            aria-label="Total"
            className="flex flex-col gap-3 rounded-2xl border border-[var(--fl-hero-border)] bg-gradient-to-br from-[var(--fl-hero-bg)] to-[var(--fl-hero-bg-2)] p-4 text-[var(--fl-hero-fg)] md:rounded-[20px] md:p-6"
          >
            <p className="text-[11.5px] font-semibold uppercase tracking-[0.08em] text-[var(--fl-hero-fg-2)]">
              {isSpending ? 'Spent' : 'Earned'} in {periodLabel}
            </p>
            <div className="flex flex-wrap items-end justify-between gap-x-10 gap-y-4">
              <div className="min-w-0">
                <p className="font-serif text-[32px] font-semibold leading-tight tabular-nums tracking-tight md:text-[40px]">
                  {formatWhole(total)}
                </p>
                <p className="mt-1 text-[13px] text-[var(--fl-hero-fg-2)]">
                  {totalChange != null && (
                    <>
                      <span
                        className={cn(
                          'font-semibold',
                          totalChange === 0
                            ? ''
                            : totalChange > 0 === isSpending
                              ? 'text-[var(--fl-hero-expense)]'
                              : 'text-[var(--fl-hero-income)]'
                        )}
                      >
                        {totalChange === 0
                          ? `Same as ${previousLabel}`
                          : `${totalChange > 0 ? '↑' : '↓'} ${formatWhole(Math.abs(totalChange))} ${
                              totalChange > 0 ? 'more' : 'less'
                            } than ${previousLabel}`}
                      </span>
                      {' · '}
                    </>
                  )}
                  <span>
                    across {rows.length} categor{rows.length === 1 ? 'y' : 'ies'}
                  </span>
                </p>
              </div>
              <dl className="grid grid-cols-2 gap-x-8 gap-y-3 sm:flex sm:gap-10">
                <HeroFact label="Biggest">
                  {rows[0].name}{' '}
                  <span className="font-sans text-[13px] font-medium text-[var(--fl-hero-fg-2)]">
                    {pct(rows[0].total)}
                  </span>
                </HeroFact>
                {grewMost?.change != null && (
                  <HeroFact label="Grew most">
                    {grewMost.name}{' '}
                    <span
                      className={cn(
                        'font-sans text-[13px] font-medium',
                        isSpending
                          ? 'text-[var(--fl-hero-expense)]'
                          : 'text-[var(--fl-hero-income)]'
                      )}
                    >
                      +{formatWhole(grewMost.change)}
                    </span>
                  </HeroFact>
                )}
                <HeroFact label="Average a day">{formatWhole(perDay)}</HeroFact>
              </dl>
            </div>
          </section>

          <section
            aria-label="By category"
            className="rounded-2xl border border-[var(--fl-border)] bg-[var(--fl-surface)] px-4 pb-2 md:px-5"
          >
            <div className="flex items-baseline justify-between gap-3 pb-1 pt-3.5">
              <h2 className="text-[15px] font-semibold text-[var(--fl-text-1)]">By category</h2>
              {previous && (
                <span className="text-xs text-[var(--fl-text-2)]">vs {previousLabel}</span>
              )}
            </div>
            <ul className="divide-y divide-[var(--fl-divider)]">
              {rows.map((r) => {
                const change = changeText(r)
                return (
                  <li key={r.categoryId ?? 'uncategorised'} className="py-3">
                    <div className="flex items-center gap-2.5 text-sm">
                      <CategoryIcon
                        icon={r.icon ?? 'tag'}
                        size={13}
                        className="flex h-7 w-7 shrink-0 items-center justify-center rounded-[9px] bg-[var(--fl-surface-subtle)] text-[var(--fl-text-2)]"
                      />
                      {r.categoryId ? (
                        <Link
                          to={transactionsLink(
                            {
                              ...periodTransactionFilter(periodType, period, fy),
                              categoryId: r.categoryId,
                              type: kind,
                            },
                            { back: ROUTES.APP.CATEGORIES }
                          )}
                          className="min-w-0 flex-1 truncate font-medium text-[var(--fl-text-1)] hover:underline"
                        >
                          {r.name}
                        </Link>
                      ) : (
                        <span className="min-w-0 flex-1 truncate font-medium text-[var(--fl-text-1)]">
                          {r.name}
                        </span>
                      )}
                      {change && (
                        <span
                          className={cn(
                            'shrink-0 text-xs font-medium tabular-nums',
                            r.isNew ? 'text-[var(--fl-text-2)]' : changeTone(r.change ?? 0)
                          )}
                        >
                          {change}
                        </span>
                      )}
                      <span className="w-20 shrink-0 text-right font-semibold tabular-nums text-[var(--fl-text-1)] md:w-24">
                        {formatWhole(r.total)}
                      </span>
                    </div>
                    <div className="ml-[38px] mt-2 flex items-center gap-3">
                      <div className="h-[7px] flex-1 rounded-full bg-[var(--fl-surface-subtle)]">
                        <div
                          className="h-full rounded-full bg-[var(--fl-hero-bar)]"
                          style={{ width: `${(r.total / largest) * 100}%` }}
                        />
                      </div>
                      <span className="w-9 shrink-0 text-right text-xs tabular-nums text-[var(--fl-text-2)]">
                        {pct(r.total)}
                      </span>
                    </div>
                  </li>
                )
              })}
            </ul>
          </section>
        </>
      )}
    </div>
  )
}

function HeroFact({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="min-w-0">
      <dt className="text-xs text-[var(--fl-hero-fg-2)]">{label}</dt>
      <dd className="mt-0.5 truncate font-serif text-lg font-semibold">{children}</dd>
    </div>
  )
}
